import React, { useEffect, useState } from "react";
import { X, CreditCard, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { paymentsConfig, createOrder, captureOrder, errText } from "../../api";

// mode: "license" | "create_user"
export default function PaymentModal({ open, onClose, mode, onDone }) {
  const [cfg, setCfg] = useState(null);
  const [days, setDays] = useState(7);
  const [nu, setNu] = useState({ email: "", password: "", name: "", role: "readonly" });
  const [busy, setBusy] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(null);

  useEffect(() => {
    if (open) {
      paymentsConfig().then(setCfg).catch(() => {});
      setPendingOrder(null);
    }
  }, [open]);

  if (!open) return null;
  const fee = cfg?.feePerDay ?? 10;
  const total = (fee * days).toFixed(2);
  const isCreate = mode === "create_user";

  const start = async () => {
    if (isCreate && (!nu.email || !nu.password)) {
      toast.error("Inserisci email e password del nuovo utente");
      return;
    }
    setBusy(true);
    try {
      const payload = isCreate
        ? { purpose: "create_user", days: Number(days), newUser: nu }
        : { purpose: "license", days: Number(days) };
      const order = await createOrder(payload);
      if (order.mock) {
        // simulated capture
        const res = await captureOrder(order.orderId);
        toast.success(isCreate ? "Utente attivato (pagamento simulato)" : "Licenza attivata (pagamento simulato)");
        onDone?.(res);
        onClose();
      } else {
        // real PayPal: open approval, then confirm capture
        if (order.approveUrl) window.open(order.approveUrl, "_blank", "noopener");
        setPendingOrder(order);
        toast.info("Completa il pagamento nella finestra PayPal, poi conferma qui");
      }
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await captureOrder(pendingOrder.orderId);
      toast.success("Pagamento confermato");
      onDone?.(res);
      onClose();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div data-testid="payment-modal" className="w-full max-w-[460px] bg-[#121824] border border-white/12 rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <CreditCard size={16} className="text-amber-400" />
            <h3 className="font-heading text-[15px] font-bold">
              {isCreate ? "Crea utente (a pagamento)" : "Acquista licenza di accesso"}
            </h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {isCreate && (
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="paid-user-email" placeholder="email utente" value={nu.email}
                onChange={(e) => setNu({ ...nu, email: e.target.value })}
                className="col-span-2 bg-[#0A1220] border border-white/12 rounded-lg py-2 px-3 text-[12px] outline-none focus:border-amber-500/50" />
              <input data-testid="paid-user-password" placeholder="password" value={nu.password}
                onChange={(e) => setNu({ ...nu, password: e.target.value })}
                className="bg-[#0A1220] border border-white/12 rounded-lg py-2 px-3 text-[12px] outline-none focus:border-amber-500/50" />
              <select data-testid="paid-user-role" value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value })}
                className="bg-[#0A1220] border border-white/12 rounded-lg py-2 px-3 text-[12px] outline-none focus:border-amber-500/50">
                <option value="readonly">readonly</option>
                <option value="trader">trader</option>
              </select>
            </div>
          )}

          <div>
            <div className="text-[10px] text-slate-400 mb-1.5">Giorni di accesso (X)</div>
            <input data-testid="payment-days-input" type="number" min={1} max={365} value={days}
              onChange={(e) => setDays(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
              className="w-full bg-[#0A1220] border border-white/12 rounded-lg py-2.5 px-3 text-[14px] font-mono text-slate-100 outline-none focus:border-amber-500/50" />
          </div>

          <div className="flex items-center justify-between rounded-lg bg-amber-500/[0.07] border border-amber-500/20 px-4 py-3">
            <span className="text-[11px] text-slate-300">{fee}€ × {days} giorni</span>
            <span data-testid="payment-total" className="font-mono text-lg font-extrabold text-amber-400">€ {total}</span>
          </div>

          {!pendingOrder ? (
            <button data-testid="pay-button" onClick={start} disabled={busy}
              className="w-full py-3 rounded-lg text-[13px] font-bold text-white bg-[#0070BA] hover:bg-[#005c98] transition-colors disabled:opacity-60">
              {busy ? "Attendere…" : cfg?.paypalConfigured ? "Paga con PayPal" : "Paga (simulato)"}
            </button>
          ) : (
            <button data-testid="confirm-payment-button" onClick={confirm} disabled={busy}
              className="w-full flex items-center justify-center gap-1.5 py-3 rounded-lg text-[13px] font-bold text-[#0B0E17] bg-gradient-to-br from-emerald-400 to-emerald-500 disabled:opacity-60">
              <CheckCircle2 size={15} /> Ho completato il pagamento
            </button>
          )}

          <p className="text-[10px] text-slate-500 leading-relaxed">
            {cfg?.paypalConfigured
              ? `PayPal ${cfg.env}. Il pagamento si apre in una nuova finestra.`
              : "PayPal non configurato: pagamento SIMULATO per il test. Aggiungi PAYPAL_CLIENT_ID e PAYPAL_SECRET per andare live."}
          </p>
        </div>
      </div>
    </div>
  );
}
